'use client';

import { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, MessageSquare, Zap, Send, Upload, X } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { getFaqs, getPage, getSettings, type Faq, type Page, type SettingsPayload } from '@/lib/cms';
import { contactApi, getApiErrorMessage } from '@/lib/api';
import toast from 'react-hot-toast';

const defaultFaqs: Faq[] = [
  { id: 0, question: 'How do I get started with EnthiosHub?', answer: 'Simply sign up for free, create your first event, and start selling tickets within minutes.' },
  { id: 1, question: 'What payment methods do you accept?', answer: 'We accept all major credit cards, debit cards, UPI, and bank transfers for Indian organizers. International payments are supported via Stripe.' },
  { id: 2, question: 'How long does it take to get paid?', answer: 'Settlements are processed within 7 business days for verified organizers.' },
  { id: 3, question: 'Can I get a refund if something goes wrong?', answer: 'Yes, we have a clear refund policy. Check our refund policy page for details.' },
];

export default function ContactPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [faqs, setFaqs] = useState<Faq[]>(defaultFaqs);

  // We are using static contact details because the CMS API currently returns a 404 for 'contact'.
  useEffect(() => {
    // getPage('contact'), getSettings(), getFaqs() removed
  }, []);

  const contactMethods = useMemo(() => {
    const email = 'support@enthioshub.in';
    const phone = '+91 9949516323';
    const address = 'Hyderabad, India';
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;

    return [
      {
        icon: Mail,
        title: 'Email Us',
        description: 'Send us an email anytime',
        value: email,
        availability: '24/7 response',
        href: gmailComposeUrl,
        external: true,
        openInNewTab: true,
      },
      /*
      // Call Us card temporarily hidden per requirements
      {
        icon: Phone,
        title: 'Call Us',
        description: 'Mon-Fri from 9am to 6pm',
        value: phone,
        availability: 'Mon-Fri, 9am-6pm IST',
        href: `tel:${phone.replace(/\s+/g, '')}`,
        external: true,
        openInNewTab: false,
      },
      */
      {
        icon: MessageSquare,
        title: 'Live Chat',
        description: 'Chat with our team',
        value: 'Start a conversation',
        availability: 'Available',
        href: '/contact/chat',
        external: false,
      },
      {
        icon: MapPin,
        title: 'Visit Us',
        description: 'Our office location',
        value: address,
        availability: 'By appointment',
        href: mapsUrl,
        external: true,
        openInNewTab: true,
      },
    ];
  }, [settings]);

  const subjectOptions = useMemo(() => {
    return [
      'General Inquiry',
      'Technical Support',
      'Sales & Pricing',
      'Partnership',
      'Other',
    ];
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [pdfAttachment, setPdfAttachment] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('subject', formData.subject);
      payload.append('message', formData.message);
      if (pdfAttachment) {
        payload.append('attachment', pdfAttachment);
      }

      await contactApi.submit(payload);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setPdfAttachment(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="px-4 py-8 sm:px-6 lg:px-8 md:py-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold font-display mb-6 text-stone-900">
              {page?.title ? (
                page.title
              ) : (
                <>Get in <span className="text-stone-500">Touch</span></>
              )}
            </h1>
            <p className="mobile-readable-copy text-xl text-stone-700 max-w-2xl mx-auto">
              {page?.meta_description || "Have questions? We're here to help. Reach out to our team and we'll get back to you as soon as possible."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 md:py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="card-hover elegant-box-hover h-full"
              >
                {method.external ? (
                  <a
                    href={method.href}
                    target={method.openInNewTab ? '_blank' : undefined}
                    rel={method.openInNewTab ? 'noopener noreferrer' : undefined}
                    className="flex flex-col justify-between h-full p-5 bg-bg border border-stone-200 rounded-xl text-center shadow-sm hover:border-stone-400 transition-colors md:rounded-2xl md:p-6"
                  >
                    <div>
                      <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <method.icon className="w-6 h-6 text-stone-700" />
                      </div>
                      <h3 className="text-lg font-semibold mb-1 text-stone-900">{method.title}</h3>
                      <p className="text-stone-600 text-sm mb-2">{method.description}</p>
                      <p className="text-stone-900 text-sm font-medium">{method.value}</p>
                    </div>
                    <p className="text-stone-500 text-xs mt-4 flex items-center justify-center pt-3 border-t border-stone-100">
                      <Clock className="w-3 h-3 mr-1" />
                      {method.availability}
                    </p>
                  </a>
                ) : (
                  <Link
                    href={method.href}
                    className="flex flex-col justify-between h-full p-5 bg-bg border border-stone-200 rounded-xl text-center shadow-sm hover:border-stone-400 transition-colors md:rounded-2xl md:p-6"
                  >
                    <div>
                      <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <method.icon className="w-6 h-6 text-stone-700" />
                      </div>
                      <h3 className="text-lg font-semibold mb-1 text-stone-900">{method.title}</h3>
                      <p className="text-stone-600 text-sm mb-2">{method.description}</p>
                      <p className="text-stone-900 text-sm font-medium">{method.value}</p>
                    </div>
                    <p className="text-stone-500 text-xs mt-4 flex items-center justify-center pt-3 border-t border-stone-100">
                      <Clock className="w-3 h-3 mr-1" />
                      {method.availability}
                    </p>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 md:py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="elegant-box-hover p-8 md:p-12 bg-bg border border-stone-200 rounded-3xl shadow-sm"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold font-display mb-2 text-stone-900">Send us a Message</h2>
              <p className="text-stone-600">Fill out the form below and we'll get back to you within 24 hours.</p>
            </div>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-secondary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-secondary-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-stone-900">Message Sent!</h3>
                <p className="text-stone-600 mb-6">Thank you for reaching out. We'll get back to you soon.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-stone-700 hover:text-stone-900 font-medium"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-stone-800">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-xl text-stone-900 focus:outline-none focus:border-stone-600"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-stone-800">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-xl text-stone-900 focus:outline-none focus:border-stone-600"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-stone-800">Subject</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-xl text-stone-900 focus:outline-none focus:border-stone-600"
                    required
                  >
                    <option value="">Select a subject</option>
                    {subjectOptions.map((subject) => (
                      <option key={subject} value={subject.toLowerCase().replace(/\s+/g, '-') }>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-stone-800">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-xl text-stone-900 focus:outline-none focus:border-stone-600 resize-none"
                    placeholder="How can we help you?"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-stone-800">Upload PDF</label>
                  <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4">
                    {pdfAttachment ? (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-stone-900">{pdfAttachment.name}</p>
                          <p className="text-xs text-stone-500">{(pdfAttachment.size / 1024 / 1024).toFixed(2)} MB PDF selected</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPdfAttachment(null)}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-300 bg-bg px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:border-stone-500"
                        >
                          <X className="h-4 w-4" />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg bg-bg px-4 py-6 text-center transition-colors hover:bg-stone-100">
                        <Upload className="h-6 w-6 text-stone-600" />
                        <span className="text-sm font-medium text-stone-900">Choose a PDF file</span>
                        <span className="text-xs text-stone-500">Optional, PDF only, up to 10 MB</span>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            if (!file) return;
                            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                              toast.error('Please upload a PDF file.');
                              e.target.value = '';
                              return;
                            }
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error('PDF must be 10 MB or smaller.');
                              e.target.value = '';
                              return;
                            }
                            setPdfAttachment(file);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="company-cta-btn mobile-tap-btn w-full py-4 bg-[#1f2a30] hover:bg-[#2a3840] text-text-inverse rounded-xl font-medium flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-text-inverse" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 md:py-16">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-surface-400">
              Quick answers to common questions
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.id ?? index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="elegant-box-hover p-6 bg-bg/5 border border-white/10 rounded-xl backdrop-blur-lg"
              >
                <h3 className="font-semibold mb-2">{faq.question}</h3>
                <p className="text-surface-400 text-sm">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
